import * as _ from 'lodash';
import * as Yup from 'yup';

import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';

const baseOrganizationValidation = Yup.object()
  .required('Required')
  .shape({
    id: Yup.string().required('Required'),
    name: Yup.string().required('Required'),
    slug: Yup.string().required('Required'),
  });

const baseUserValidation = Yup.object()
  .required('Required')
  .shape({
    id: Yup.string().required('Required'),
    firstName: Yup.string().required('Required'),
    lastName: Yup.string().required('Required'),
    email: Yup.string().required('Required').email('must be valid email'),
  });

const baseBankAccountValidation = Yup.object()
  .required('Required')
  .shape({
    id: Yup.string().required('Required'),
    accountNumber: Yup.string().required('Required'),
    routingNumber: Yup.string().required('Required'),
    bankInstitutionKey: Yup.string().required('Required'),
    name: Yup.string().required('Required').nullable(),
  });

const baseBankAccountEntityValidation = Yup.object()
  .required('Required')
  .shape({
    entity: baseBankAccountValidation,
    _isWrittenIn_: Yup.boolean().required('Required'),
    type: Yup.object().required('Required'),
    section: Yup.string().nullable(),
  });
const baseUserEntityValidation = Yup.object()
  .required('Required')
  .shape({
    entity: baseUserValidation,
    primaryContact: Yup.object().required('Required'),
    section: Yup.string().nullable(),
    _isWrittenIn_: Yup.boolean().required('Required'),
  });

const basePartyEntityValidation = Yup.object()
  .required('Required')
  .shape({
    entity: baseOrganizationValidation,
    _isWrittenIn_: Yup.boolean().required('Required'),
    type: Yup.string()
      .required()
      .test('partyType', 'Must be valid party type', (val) =>
        _.includes(_.values(CreditAgreement.DomainEntity.Items.PartyType), val),
      ),
    users: Yup.array().of(baseUserEntityValidation).required('Required'),
    section: Yup.string().nullable(),
  });

const borrowerEntity = Yup.object().shape({
  entity: baseOrganizationValidation,
  _isWrittenIn_: Yup.boolean().required('Required'),
  type: Yup.string()
    .required()
    .test(
      'borrowerPartyType',
      'must be borrowerPartyType',
      (val) => val === 'borrower',
    ),
  users: Yup.array().of(baseUserEntityValidation).required('Required'),
  bankAccounts: Yup.array()
    .of(baseBankAccountEntityValidation)
    .required('Required'),
  borrowerEntityName: Yup.object().required('Required'),
  section: Yup.string().nullable(),
});

const capitalProviderEntity = Yup.object().shape({
  entity: baseOrganizationValidation,
  _isWrittenIn_: Yup.boolean().required('Required'),
  type: Yup.string()
    .required()
    .test(
      'capitalProviderPartyType',
      'must be capitalProviderType',
      (val) => val === 'capitalProvider',
    ),
  users: Yup.array().of(baseUserEntityValidation).required('Required'),
  bankAccounts: Yup.array()
    .of(baseBankAccountEntityValidation)
    .required('Required'),
  commitmentAmount: Yup.object().required('Required'),
  section: Yup.string().nullable(),
  _config_: Yup.object()
    .required('Required')
    .shape({
      interestRateBase: Yup.string()
        .required('Required')
        .test(
          'interestRateBase',
          'interest rate base must be fixed or benchmark',
          (val) => val === 'fixed' || val === 'benchmark',
        ),
    }),
});

const wipFormValidation = Yup.object().shape({
  id: Yup.string().required('Required'),
  creditFacilityId: Yup.string().required('Required'),
  effectiveFrom: Yup.number().required('Required'),
  effectiveTo: Yup.number().required('Required'),
  version: Yup.string()
    .required('Required')
    .test('parseNumber', 'Version must be parsable to a number', (val) => {
      return _.isFinite(_.toNumber(val));
    }),
  _schemaVersion_: Yup.string().required('Required'),
  s3Path: Yup.string().optional(),
  facilityMechanics: Yup.object().shape({
    // required
    closingDate: Yup.object().required('Required'),
    finalMaturityDate: Yup.object().required('Required'),

    // optional
    amortizationPeriodStart: Yup.object().shape({}).nullable(),
    amortizationPeriodEnd: Yup.object().shape({}).nullable(),
    callProtectionExpirationDate: Yup.object().shape({}).nullable(),
    callProtectionValue: Yup.object().shape({}).nullable(),
    commitmentPeriodEffectiveDate: Yup.object().shape({}).nullable(),
    interestRateCeiling: Yup.object().shape({}).nullable(),
    externalFacilityId: Yup.object().shape({}).nullable(),
    governingLaw: Yup.object().shape({}).nullable(),
    minimumUtilization: Yup.object().shape({}).nullable(),
    revolvingCommitmentTermination: Yup.object().shape({}).nullable(),
    seniority: Yup.object().shape({}).nullable(),
    yieldMaintenance: Yup.object().shape({}).nullable(),
    // parties
    parties: Yup.object()
      .required('Required')
      .shape({
        adminAgents: Yup.array()
          .required('Required')
          .of(basePartyEntityValidation),
        backupServicers: Yup.array()
          .required('Required')
          .of(basePartyEntityValidation),
        borrowers: Yup.array()
          .required('Required')
          .of(borrowerEntity)
          .test(
            'borrowersLength',
            'must be only one borrower',
            (arr) => !!arr && arr.length === 1,
          ),
        capitalProviders: Yup.array()
          .required('Required')
          .of(capitalProviderEntity)
          .test(
            'capitalProvidersLength',
            'must be at least one borrower',
            (arr) => !!arr && arr.length > 0,
          ),
        capitalProviderRepresentatives: Yup.array()
          .required('Required')
          .of(basePartyEntityValidation)
          .test(
            'capitalProviderRepresentative',
            'must be at one capitalProviderRepresentatives',
            (arr) => !!arr && arr.length === 1,
          ),
        collectionAccountBanks: Yup.array()
          .required('Required')
          .of(basePartyEntityValidation),
        payingAgents: Yup.array()
          .required('Required')
          .of(basePartyEntityValidation),
        servicers: Yup.array()
          .required('Required')
          .of(basePartyEntityValidation),
        verificationAgents: Yup.array()
          .required('Required')
          .of(basePartyEntityValidation),
      }),
  }),
});

export const createWipSchemaValidation = (): any => wipFormValidation;
